/**
 * MarketplacePage.tsx — the Marketplace dashboard.
 *
 * The apps set up for this practice, with order totals across them on top. Built
 * from the Marketplace redesign handoff, mapped onto the app's own theme:
 * slate for the design's navy and greys, the account's `primary` for its
 * accent, and Inter throughout.
 *
 *   header (title, search) → orders across every app (tiles, recent orders,
 *   recent cancellations) → Your apps (grid or list)
 *
 * Every app has a main button. One with a page of its own (Peptides, Partell,
 * Vitaboom) opens it; a lab, ordered from the patient's chart, opens the
 * Marketplace Orders page on its own orders.
 * Clicking a card, or Details, opens the details pop-up, which only describes
 * the app; setup stays under Admin → Marketplace.
 *
 * Every switched-on app shows (marketplaceApps.ts). A ready one — set up for
 * the practice — opens its page; one that is not shows Connect, which takes an
 * admin to its setup screen and tells anyone else an admin has to finish it.
 * Only ready apps count as installed.
 *
 * The summary tiles open the Marketplace Orders page on their preset, and a
 * recent order opens the patient's chart where that order is, by patient ID.
 *
 * Anyone can browse. Order numbers and the main buttons need user level 2, the
 * level of the pages they open; below it the main button opens the details.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search, Store, CheckCircle2, Info, LayoutGrid, List,
  ShoppingBag, CalendarPlus, XCircle, Clock, ChevronRight, Plug,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { cn, parseDateValue } from '@/lib/utils';
import { getAccountIntegrationOrders, type IntegrationOrder } from '@/services/integrations.service';
import { useAuthStore } from '@/store/authStore';
import { deriveIsSuperAdmin } from '@/hooks/useNavAccess';
import { CATEGORY_LABELS, type MarketplaceApp, type MarketplaceCategory } from './marketplaceCatalog';
import {
  ORDERS_LEVEL,
  appsByVendorKey,
  isOrderCancelled,
  marketplaceOrdersHref,
  orderCancelledAt,
  orderChartHref,
  useMarketplaceApps,
  useOrderScope,
  type MarketplaceOrdersView,
} from './marketplaceApps';

/** Setup screens are admin-only (navigation.config: Admin minUserLevel 3). */
const ADMIN_LEVEL = 3;

type CategoryFilter = 'all' | MarketplaceCategory;
type View = 'grid' | 'list';

/** The handoff's 120ms colour transition and easing. */
// Arbitrary properties: Tailwind reads `duration-[…]` / `ease-[…]` as ambiguous and drops them.
const EASE = 'transition-colors [transition-duration:120ms] [transition-timing-function:cubic-bezier(0.2,0.8,0.2,1)]';
/** The handoff's card shadow, in slate-900 (rgb 15 23 42) for its navy. */
const CARD_SHADOW = 'shadow-[0_1px_2px_rgba(15,23,42,0.06)]';
const FOCUS = 'focus:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/40';
/** Every button keeps the app's 44px touch floor below md (components/ui/Button.tsx). */
const TOUCH = 'max-md:min-h-[44px]';

/**
 * Orders per app id, from the account's marketplace orders (each carries its
 * `vendorKey`). Counted like the Peptides page's Total Orders: every order in
 * scope, whatever its status. An app with no vendor key (Vitaboom keeps its
 * orders on its own side) gets no entry, so its card shows no count.
 */
export function orderCountsByApp(
  apps: MarketplaceApp[],
  orders: IntegrationOrder[],
  inScope: (o: IntegrationOrder) => boolean = () => true,
): Record<string, number> {
  const appByKey = appsByVendorKey(apps);
  const counts: Record<string, number> = {};
  for (const app of apps) if (app.integrationKey) counts[app.id] = 0;
  for (const o of orders) {
    const app = o.vendorKey ? appByKey.get(o.vendorKey) : undefined;
    if (app && inScope(o)) counts[app.id] += 1;
  }
  return counts;
}

export interface MarketplaceOrderSummary {
  total: number;
  addedThisMonth: number;
  cancelled: number;
  awaitingPayment: number;
  /** Newest first, at most `limit`. */
  recent: IntegrationOrder[];
  /** Most recently cancelled first, at most `limit`. */
  recentCancelled: IntegrationOrder[];
}

const RECENT_LIMIT = 5;

/**
 * The summary across every app shown: the same orders the cards count (in
 * scope, belonging to a set-up app), so the tiles add up to the cards.
 * Awaiting payment is the payment gate every vendor shares — the order is held
 * in 'awaiting_payment' until its fee is collected — as on the Peptides page.
 */
export function marketplaceOrderSummary(
  apps: MarketplaceApp[],
  orders: IntegrationOrder[],
  inScope: (o: IntegrationOrder) => boolean = () => true,
  now: Date = new Date(),
  limit = RECENT_LIMIT,
): MarketplaceOrderSummary {
  const appByKey = appsByVendorKey(apps);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const time = (value: string | null | undefined) => parseDateValue(value)?.getTime() ?? 0;
  const mine = orders.filter((o) => o.vendorKey && appByKey.has(o.vendorKey) && inScope(o));
  const cancelled = mine.filter(isOrderCancelled);
  return {
    total: mine.length,
    addedThisMonth: mine.filter((o) => time(o.createdAt) >= monthStart).length,
    cancelled: cancelled.length,
    awaitingPayment: mine.filter((o) => o.fulfillmentStatus === 'awaiting_payment').length,
    recent: [...mine].sort((a, b) => time(b.createdAt) - time(a.createdAt)).slice(0, limit),
    recentCancelled: [...cancelled]
      .sort((a, b) => time(orderCancelledAt(b) ?? b.createdAt) - time(orderCancelledAt(a) ?? a.createdAt))
      .slice(0, limit),
  };
}

/** "5 apps installed · 2 need setup"; under a category, "3 apps · 1 needs setup". Ready apps only are installed. */
export function appCountLabel(ready: number, needSetup: number, all: boolean): string {
  const installed = `${ready} app${ready === 1 ? '' : 's'}${all ? ' installed' : ''}`;
  return needSetup === 0 ? installed : `${installed} · ${needSetup} need${needSetup === 1 ? 's' : ''} setup`;
}

/** "59 orders", "1 order", "No orders". */
export function ordersLabel(orders: number): string {
  if (orders === 0) return 'No orders';
  return `${orders.toLocaleString()} order${orders === 1 ? '' : 's'}`;
}

/**
 * The apps the search and category pill leave, most orders first (the
 * handoff's default sort). Search matches name or description, ignoring case.
 * An app with no count (Vitaboom, or before the counts load) sorts after the
 * counted ones; ties keep the catalogue's order.
 */
export function visibleApps(
  apps: MarketplaceApp[],
  query: string,
  category: CategoryFilter,
  orderCounts: Record<string, number>,
): MarketplaceApp[] {
  const q = query.trim().toLowerCase();
  return apps
    .filter((app) => (category === 'all' || app.category === category)
      && (!q || app.name.toLowerCase().includes(q) || app.tagline.toLowerCase().includes(q)))
    .sort((a, b) => (orderCounts[b.id] ?? -1) - (orderCounts[a.id] ?? -1));
}

export default function MarketplacePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [view, setView] = useState<View>('grid');
  const [selected, setSelected] = useState<MarketplaceApp | null>(null);

  const userLevel = user?.userLevel ?? 0;
  const canSeeOrders = userLevel >= ORDERS_LEVEL;
  const isAdmin = deriveIsSuperAdmin(user) || userLevel >= ADMIN_LEVEL;

  // `apps` (the ready ones) are what counts: installed, ordered from, summed.
  const { switchedOn, ready: apps, checking } = useMarketplaceApps();
  const readyIds = useMemo(() => new Set(apps.map((a) => a.id)), [apps]);

  // Every marketplace order on the account in one call, counted per app.
  const ordersQuery = useQuery({
    queryKey: ['account-integration-orders', 'all'],
    queryFn: () => getAccountIntegrationOrders(),
    staleTime: 30_000,
    enabled: canSeeOrders && apps.some((app) => app.integrationKey),
  });
  const inScope = useOrderScope();
  const orderCounts = useMemo(
    () => (ordersQuery.data ? orderCountsByApp(apps, ordersQuery.data, inScope) : {}),
    [apps, ordersQuery.data, inScope],
  );

  const summary = useMemo(
    () => (ordersQuery.data ? marketplaceOrderSummary(apps, ordersQuery.data, inScope) : null),
    [apps, ordersQuery.data, inScope],
  );
  const appByKey = useMemo(() => appsByVendorKey(apps), [apps]);
  const showOrderSummary = canSeeOrders && apps.some((app) => app.integrationKey);

  const shown = visibleApps(switchedOn, search, category, orderCounts);
  const countLabel = appCountLabel(
    shown.filter((app) => readyIds.has(app.id)).length,
    shown.filter((app) => !readyIds.has(app.id)).length,
    category === 'all',
  );

  /** The card's main button. Below the app pages' level it opens the details instead. */
  const runAction = (app: MarketplaceApp) => {
    if (!canSeeOrders) setSelected(app);
    else if (app.action) navigate(app.action.href);
  };
  /** Connect: an admin goes to the app's setup screen; anyone else reads who has to do it. */
  const connect = (app: MarketplaceApp) => {
    if (isAdmin) navigate(app.setupHref);
    else setSelected(app);
  };


  return (
    <div className="mx-auto flex max-w-[1440px] flex-col gap-7 p-8 pb-16 max-md:p-4">
      {/* ── 1. Header ────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[30px] font-bold leading-[1.15] tracking-[-0.02em] text-slate-900">Marketplace</h1>
        </div>
        <div className="relative w-[260px] max-w-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search apps"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-[10px] border-slate-300 py-[9px] pl-9 pr-3 text-sm"
            aria-label="Search apps"
          />
        </div>
      </div>

      {/* ── 2. Orders across every app ───────────────────────────────────── */}
      {showOrderSummary && (
        <OrderSummary
          summary={summary}
          loading={ordersQuery.isLoading}
          appByKey={appByKey}
          onOpenView={(view) => navigate(marketplaceOrdersHref(view))}
          onOpenOrder={(href) => navigate(href)}
        />
      )}

      {/* ── 3. Your apps ─────────────────────────────────────────────────── */}
      <section aria-labelledby="mp-your-apps" className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-baseline gap-2.5">
            <h2 id="mp-your-apps" className="text-xl font-bold tracking-[-0.01em] text-slate-900">Your apps</h2>
            {!checking && switchedOn.length > 0 && <span className="text-[13px] text-slate-500">{countLabel}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Category">
              {(['all', ...(Object.keys(CATEGORY_LABELS) as MarketplaceCategory[])] as CategoryFilter[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  aria-pressed={category === c}
                  className={cn(
                    'rounded-full border px-3.5 py-1.5 text-[13px] font-bold',
                    EASE, FOCUS, TOUCH,
                    category === c
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-slate-300 bg-white text-slate-900 hover:bg-slate-100',
                  )}
                >
                  {c === 'all' ? 'All' : CATEGORY_LABELS[c]}
                </button>
              ))}
            </div>
            <span className="mx-1.5 h-5 w-px bg-slate-200" aria-hidden="true" />
            <div className="inline-flex overflow-hidden rounded-[10px] border border-slate-300 bg-white" role="group" aria-label="View">
              {([['grid', LayoutGrid, 'Grid view'], ['list', List, 'List view']] as const).map(([v, Icon, label]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  aria-pressed={view === v}
                  aria-label={label}
                  className={cn(
                    'inline-flex items-center px-[9px] py-[7px] text-slate-900',
                    EASE, FOCUS, TOUCH,
                    view === v ? 'bg-slate-100' : 'bg-white hover:bg-slate-50',
                  )}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {checking ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : switchedOn.length === 0 ? (
          <EmptyState
            icon={<Store className="h-10 w-10" />}
            title="No apps are switched on for your practice yet"
            description="Contact the Prospera team to add labs, pharmacy or supplement apps to your account."
          />
        ) : shown.length === 0 ? (
          <div className="rounded-[14px] border border-dashed border-slate-300 bg-[radial-gradient(rgba(15,23,42,0.06)_1px,transparent_1px)] bg-[length:24px_24px] p-12 text-center text-sm text-slate-500">
            {search.trim()
              ? `No apps match "${search.trim()}" — try a different name or clear the filter.`
              : 'No apps match — try a different name or clear the filter.'}
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4 max-md:grid-cols-1">
            {shown.map((app) => (
              <AppGridCard
                key={app.id}
                app={app}
                ready={readyIds.has(app.id)}
                orders={orderCounts[app.id]}
                onOpen={() => setSelected(app)}
                onAction={() => runAction(app)}
                onConnect={() => connect(app)}
              />
            ))}
          </div>
        ) : (
          <div className={cn('overflow-hidden rounded-[14px] border border-slate-200 bg-white', CARD_SHADOW)}>
            {shown.map((app) => (
              <AppListRow
                key={app.id}
                app={app}
                ready={readyIds.has(app.id)}
                orders={orderCounts[app.id]}
                onOpen={() => setSelected(app)}
                onAction={() => runAction(app)}
                onConnect={() => connect(app)}
              />
            ))}
          </div>
        )}
      </section>

      <AppDetailsModal
        app={selected}
        ready={selected ? readyIds.has(selected.id) : true}
        isAdmin={isAdmin}
        onClose={() => setSelected(null)}
        onConnect={(app) => { setSelected(null); navigate(app.setupHref); }}
      />

    </div>
  );
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

/** Each tile, and the Marketplace Orders preset it opens. */
const SUMMARY_TILES: Array<{
  key: keyof Omit<MarketplaceOrderSummary, 'recent' | 'recentCancelled'>;
  view: MarketplaceOrdersView;
  label: string;
  icon: JSX.Element;
}> = [
  { key: 'total', view: 'all', label: 'Total orders', icon: <ShoppingBag className="h-4 w-4" /> },
  { key: 'addedThisMonth', view: 'month', label: 'Added this month', icon: <CalendarPlus className="h-4 w-4" /> },
  { key: 'cancelled', view: 'cancelled', label: 'Cancelled', icon: <XCircle className="h-4 w-4" /> },
  { key: 'awaitingPayment', view: 'awaiting', label: 'Awaiting payment', icon: <Clock className="h-4 w-4" /> },
];

/** "Aug 8, 2026", as the Memberships dashboard writes it. */
function formatOrderDate(value: string | null | undefined): string {
  const date = parseDateValue(value);
  return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '\u2014';
}

/**
 * Totals across every app, then the latest orders and cancellations — the
 * Memberships dashboard's shape. A tile opens Marketplace Orders on its preset;
 * an order opens the patient's chart where it is.
 */
function OrderSummary({
  summary,
  loading,
  appByKey,
  onOpenView,
  onOpenOrder,
}: {
  summary: MarketplaceOrderSummary | null;
  loading: boolean;
  appByKey: Map<string, MarketplaceApp>;
  onOpenView: (view: MarketplaceOrdersView) => void;
  onOpenOrder: (href: string) => void;
}) {
  return (
    <section aria-label="Orders across every app" className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {SUMMARY_TILES.map((tile) => (
          <button
            key={tile.key}
            type="button"
            onClick={() => onOpenView(tile.view)}
            className={cn('rounded-[14px] border border-slate-200 bg-white p-5 text-left hover:bg-slate-100', CARD_SHADOW, EASE, FOCUS)}
          >
            <span className="flex items-start justify-between gap-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">{tile.label}</span>
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-slate-100 text-slate-900" aria-hidden="true">
                {tile.icon}
              </span>
            </span>
            <span className="mt-2 block text-[34px] font-bold leading-[1.1] tracking-[-0.02em] text-slate-900 tabular-nums">
              {loading || !summary ? <Spinner size="sm" /> : summary[tile.key].toLocaleString()}
            </span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <OrderListCard
          title="Recent orders"
          subtitle="The latest orders across every app."
          empty="No orders yet."
          orders={summary?.recent ?? []}
          loading={loading}
          appByKey={appByKey}
          cancellations={false}
          onOpenOrder={onOpenOrder}
        />
        <OrderListCard
          title="Recent cancellations"
          subtitle="The most recently cancelled orders."
          empty="No cancellations."
          orders={summary?.recentCancelled ?? []}
          loading={loading}
          appByKey={appByKey}
          cancellations
          onOpenOrder={onOpenOrder}
        />
      </div>
    </section>
  );
}

function OrderListCard({
  title,
  subtitle,
  empty,
  orders,
  loading,
  appByKey,
  cancellations,
  onOpenOrder,
}: {
  title: string;
  subtitle: string;
  empty: string;
  orders: IntegrationOrder[];
  loading: boolean;
  appByKey: Map<string, MarketplaceApp>;
  cancellations: boolean;
  onOpenOrder: (href: string) => void;
}) {
  return (
    <div className={cn('overflow-hidden rounded-[14px] border border-slate-200 bg-white', CARD_SHADOW)}>
      <div className="border-b border-slate-100 px-5 py-4">
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
        <p className="mt-0.5 text-[13px] text-slate-600">{subtitle}</p>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner size="sm" /></div>
      ) : orders.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-500">{empty}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {orders.map((o) => {
            const app = o.vendorKey ? appByKey.get(o.vendorKey) : undefined;
            const cancelled = isOrderCancelled(o);
            const cancelledAt = orderCancelledAt(o);
            const when = cancellations && cancelledAt
              ? `Cancelled ${formatOrderDate(cancelledAt)}`
              : formatOrderDate(o.createdAt);
            const href = orderChartHref(app, o);
            const body = (
              <>
                {app && <AppTile app={app} size="sm" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-slate-900">{o.patientName || 'Unknown patient'}</span>
                  <span className="mt-0.5 block truncate text-xs text-slate-500">{app?.name ?? o.vendorLabel ?? 'Order'} • {when}</span>
                </span>
                {cancelled ? (
                  <Badge variant="error" dot>Cancelled</Badge>
                ) : o.fulfillmentStatus === 'awaiting_payment' ? (
                  <Badge variant="warning" dot>Awaiting payment</Badge>
                ) : (
                  <Badge variant="success" dot>Ordered</Badge>
                )}
              </>
            );
            return (
              <li key={o.integrationOrderId}>
                {href ? (
                  <button
                    type="button"
                    onClick={() => onOpenOrder(href)}
                    aria-label={`Open ${o.patientName || 'the patient'}'s ${app?.name ?? ''} orders`}
                    className={cn('flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-slate-50', EASE, FOCUS)}
                  >
                    {body}
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                  </button>
                ) : (
                  <div className="flex items-center gap-3 px-5 py-3">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * The app's tile: its category icon, one size per place (48px on cards and in
 * the pop-up, 40px in rows). The same colours for every app.
 */
export function AppTile({ app, size = 'md' }: { app: MarketplaceApp; size?: 'md' | 'sm' }) {
  const Icon = app.icon;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center',
        size === 'md' ? 'h-12 w-12 rounded-xl' : 'h-10 w-10 rounded-[10px]',
        'bg-slate-100 text-slate-900',
      )}
      aria-hidden="true"
    >
      <Icon className={size === 'md' ? 'h-6 w-6' : 'h-5 w-5'} />
    </span>
  );
}

function CategoryLine({ app, className }: { app: MarketplaceApp; className?: string }) {
  const Icon = app.icon;
  return (
    <span className={cn('inline-flex items-center gap-[5px] text-xs text-slate-600', className)}>
      <Icon className="h-[13px] w-[13px]" /> {CATEGORY_LABELS[app.category]}
    </span>
  );
}

/** "59 orders" / "No orders" / "Needs setup"; nothing for an app whose orders we don't hold or can't show. */
function OrdersPill({ orders, ready }: { orders?: number; ready: boolean }) {
  if (!ready) return <Badge variant="warning" className="whitespace-nowrap">Needs setup</Badge>;
  if (orders === undefined) return null;
  return (
    <span
      className={cn(
        'whitespace-nowrap rounded-full px-[9px] py-[3px] text-xs font-semibold tabular-nums',
        orders > 0 ? 'bg-primary/10 text-slate-900' : 'bg-slate-100 text-slate-500',
      )}
    >
      {ordersLabel(orders)}
    </span>
  );
}

interface AppItemProps {
  app: MarketplaceApp;
  /** Set up for the practice: Open; otherwise Connect. */
  ready: boolean;
  orders?: number;
  /** Card or row click, and Details: the details pop-up. */
  onOpen: () => void;
  /** The main button of a ready app. */
  onAction: () => void;
  /** Connect, on an app not set up yet. */
  onConnect: () => void;
}

/** The main and Details buttons; a click on either never also opens the card. */
function AppButtons({ app, ready, onOpen, onAction, onConnect, compact = false }: AppItemProps & { compact?: boolean }) {
  const base = cn('font-bold', compact ? 'rounded-lg px-3 py-[7px] text-xs' : 'rounded-[10px] px-3 py-2 text-[13px]', EASE, FOCUS, TOUCH);
  const hasMain = !ready || !!app.action;
  return (
    <>
      {!ready ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onConnect(); }}
          className={cn(base, 'inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800', !compact && 'flex-1')}
        >
          <Plug className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden="true" /> Connect
        </button>
      ) : app.action && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAction(); }}
          className={cn(base, 'bg-slate-900 text-white hover:bg-slate-800', !compact && 'flex-1')}
        >
          {app.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
        className={cn(base, 'border border-slate-300 bg-white text-slate-900 hover:bg-slate-100', !compact && !hasMain && 'flex-1')}
      >
        Details
      </button>
    </>
  );
}

/** What finishing setup involves, from what makes an app ready (marketplaceApps.ts setUpApps). */
function setupSteps(app: MarketplaceApp): string {
  return app.requiresOnboarding
    ? 'Switch on at least one product and complete the onboarding there.'
    : 'Switch on at least one product there.';
}

/** Opens the details on Enter or Space, like the button the card stands in for. */
function openOnKey(onOpen: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
  };
}

function AppGridCard(props: AppItemProps) {
  const { app, ready, orders, onOpen } = props;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${app.name} details`}
      onClick={onOpen}
      onKeyDown={openOnKey(onOpen)}
      className={cn(
        'flex cursor-pointer flex-col gap-3.5 rounded-[14px] border border-slate-200 bg-white p-5 hover:bg-slate-100',
        CARD_SHADOW, EASE, FOCUS,
      )}
    >
      <div className="flex items-start gap-3.5">
        <AppTile app={app} />
        <div className="min-w-0 flex-1">
          <p className="text-base font-bold leading-[1.2] text-slate-900">{app.name}</p>
          <CategoryLine app={app} className="mt-1" />
        </div>
        <OrdersPill orders={orders} ready={ready} />
      </div>
      <p className="min-h-[42px] text-sm leading-normal text-slate-600 [text-wrap:pretty]">{app.tagline}</p>
      <div className="mt-auto flex items-center gap-2">
        <AppButtons {...props} />
      </div>
    </div>
  );
}

function AppListRow(props: AppItemProps) {
  const { app, ready, orders, onOpen } = props;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${app.name} details`}
      onClick={onOpen}
      onKeyDown={openOnKey(onOpen)}
      className={cn(
        'grid cursor-pointer grid-cols-[40px_minmax(140px,1fr)_minmax(0,2fr)_110px_auto] items-center gap-4 border-b border-slate-100 px-5 py-3.5 last:border-b-0 hover:bg-slate-50',
        // Phone: tile and name on one line, the rest beneath, so nothing scrolls sideways.
        'max-md:grid-cols-[40px_minmax(0,1fr)] max-md:gap-x-3 max-md:gap-y-2 max-md:px-4',
        EASE, FOCUS,
      )}
    >
      <AppTile app={app} size="sm" />
      <div className="min-w-0">
        <p className="text-[15px] font-bold leading-[1.2] text-slate-900">{app.name}</p>
        <CategoryLine app={app} className="mt-0.5" />
      </div>
      <p className="truncate text-sm text-slate-600 max-md:col-span-2">{app.tagline}</p>
      <div className="justify-self-start max-md:col-span-2"><OrdersPill orders={orders} ready={ready} /></div>
      <div className="flex gap-2 max-md:col-span-2">
        <AppButtons {...props} compact />
      </div>
    </div>
  );
}

/**
 * The details modal: what the app does and what to know. For an app not set up
 * yet it also says what is left to do, and offers an admin Connect.
 */
function AppDetailsModal({
  app,
  ready,
  isAdmin,
  onClose,
  onConnect,
}: {
  app: MarketplaceApp | null;
  ready: boolean;
  isAdmin: boolean;
  onClose: () => void;
  onConnect: (app: MarketplaceApp) => void;
}) {
  return (
    <Modal
      isOpen={app !== null}
      onClose={onClose}
      size="lg"
      title={app ? (
        <span className="flex items-center gap-3.5">
          <AppTile app={app} />
          <span className="flex flex-col">
            <span className="text-base font-bold leading-[1.2] text-slate-900">{app.name}</span>
            <CategoryLine app={app} className="mt-1 font-normal" />
          </span>
        </span>
      ) : ''}
      footer={app ? (
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {!ready && isAdmin && (
            <Button leftIcon={<Plug className="h-4 w-4" />} onClick={() => onConnect(app)}>Connect</Button>
          )}
        </div>
      ) : undefined}
    >
      {app && (
        <div className="space-y-5">
          {!ready && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
              <p className="font-semibold">{app.name} isn't set up for your practice yet.</p>
              <p className="mt-1">
                {isAdmin
                  ? `Connect opens its setup screen under Admin → Marketplace. ${setupSteps(app)}`
                  : `An admin at your practice needs to finish setting it up under Admin → Marketplace. ${setupSteps(app)}`}
              </p>
            </div>
          )}
          <p className="text-sm leading-relaxed text-slate-600">{app.description}</p>

          {app.features.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-bold text-slate-900">What you get</h3>
              <ul className="space-y-1.5">
                {app.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {app.goodToKnow.length > 0 && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
              <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-blue-900">
                <Info className="h-4 w-4" /> Good to know
              </h3>
              <ul className="space-y-1 text-sm text-blue-900/90">
                {app.goodToKnow.map((g) => <li key={g}>{g}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
